<script setup>
import { computed } from 'vue'
import { Badge, Table } from '@drama/ui'

import AuditCard from './AuditCard.vue'
import AuditId from './AuditId.vue'
import EvidenceList from './EvidenceList.vue'
import { useAuditText } from './audit-text.js'
import { activePreferences, asArray, characterName, directorState } from './run-observer.js'

const props = defineProps({
  snapshot: { type: Object, required: true },
  pack: { type: Object, required: true },
  branch: { type: Object, required: true },
  sources: { type: Map, required: true },
  // 分支偏好哈希的计算状态 { status: 'pending'|'ready'|'failed', hash, reason }，页面异步算好后更新。
  preference: { type: Object, required: true },
})

const STATUS_VARIANTS = { ready: 'success', available: 'success', injected_direction: 'success', pending: 'warning', not_ready: 'warning' }

const { tr, enumLabel, rangeLabel } = useAuditText()
const director = computed(() => directorState(props.snapshot, props.pack, props.branch, props.preference.hash))
const guidance = computed(() => director.value?.guidance ?? props.branch.director_guidance ?? null)
const preferences = computed(() => activePreferences(props.branch))
const deliveries = computed(() => asArray(props.branch.turns).map((turn, index) => ({
  key: turn.turn_id || `${turn.seq ?? index}`,
  seq: turn.seq,
  delivery: turn.director || null,
})))
const columns = computed(() => [
  { key: 'seq', label: tr('director.turn'), width: '100px' },
  { key: 'delivery', label: tr('director.delivery'), width: '240px' },
  { key: 'passed', label: tr('director.passedWriter'), width: '140px' },
  { key: 'reason', label: tr('director.reason'), width: '380px', wrap: true },
])
const switchLabel = computed(() => {
  if (director.value?.enabled === true) return tr('director.on')
  if (director.value?.enabled === false) return tr('director.off')
  return tr('director.unknown')
})

/** 导演状态芯片：开关开着时要等偏好哈希算出来才能判断建议是否可用；浏览器算不出来就明说无法计算。 */
const statusBadge = computed(() => {
  const status = director.value?.status
  if (status) return { label: statusLabel(status), variant: STATUS_VARIANTS[status] ?? 'neutral', title: statusHint(status) }
  if (director.value && props.preference.status === 'failed') {
    return { label: tr('director.hashFailed'), variant: 'danger', title: `${tr('director.hashFailedHint')} ${props.preference.reason}`.trim() }
  }
  if (director.value) return { label: tr('director.hashPending'), variant: 'neutral', title: undefined }
  return { label: statusLabel(null), variant: 'neutral', title: undefined }
})

function statusLabel(status) {
  return status ? enumLabel('director.statuses', status) : tr('director.notRecorded')
}

function statusHint(status) {
  return status ? enumLabel('director.statusHints', status) : undefined
}

/** 只凭这一轮保存的注入记录判断是否传给了写作者；正文不能证明模型采用了建议。 */
function passedLabel(delivery) {
  if (!delivery) return tr('director.notRecorded')
  return String(delivery.status || '').startsWith('injected_') ? tr('director.yes') : tr('director.no')
}

/** 偏好值：节奏取文案，关注人物取人名，其余是玩家写的原文。 */
function preferenceValue(preference) {
  if (preference.kind === 'pace') return enumLabel('preferences.paces', preference.value)
  if (preference.kind === 'focus') return characterName(props.pack, preference.value)
  return preference.value
}

function preferenceSource(preference) {
  return tr(preference.source === 'explicit_ooc_expectation' ? 'preferences.sources.explicit_ooc_expectation' : 'preferences.sources.explicit_ui')
}
</script>

<template>
  <div class="au-tab" data-testid="observer-director-panel">
    <div class="au-chips">
      <Badge size="sm" :variant="director?.enabled ? 'success' : 'neutral'">{{ switchLabel }}</Badge>
      <Badge size="sm" :variant="statusBadge.variant" :title="statusBadge.title" data-testid="observer-director-status">{{ statusBadge.label }}</Badge>
      <Badge size="sm">{{ tr('director.revision', { revision: director?.revision ?? '—' }) }}</Badge>
    </div>
    <div class="au-split">
      <AuditCard :title="tr('director.latest')">
        <template v-if="guidance">
          <p class="au-body">{{ guidance.direction || '—' }}</p>
          <dl class="au-kv">
            <dt>{{ tr('director.directionStatus') }}</dt>
            <dd>
              <Badge size="sm" :variant="STATUS_VARIANTS[guidance.direction_status] ?? 'neutral'" :title="statusHint(guidance.direction_status)">
                {{ statusLabel(guidance.direction_status) }}
              </Badge>
            </dd>
            <dt>{{ tr('director.basis') }}</dt>
            <dd>{{ rangeLabel(guidance) }}</dd>
            <dt>{{ tr('director.validThrough') }}</dt>
            <dd>{{ guidance.valid_through_seq == null ? tr('director.notRecorded') : tr('sources.turn', { count: guidance.valid_through_seq }) }}</dd>
            <dt>{{ tr('director.guidanceId') }}</dt>
            <dd><AuditId v-if="guidance.guidance_id" :id="guidance.guidance_id" /><span v-else>{{ tr('director.notRecorded') }}</span></dd>
          </dl>
          <details v-if="asArray(guidance.evidence_ids).length" class="au-evidence">
            <summary>{{ tr('sources.evidence', { count: guidance.evidence_ids.length }) }}</summary>
            <EvidenceList :record="guidance" :sources="sources" />
          </details>
        </template>
        <p v-else class="au-empty">{{ tr('empty') }}</p>
      </AuditCard>
      <AuditCard :title="tr('preferences.title')" data-testid="observer-experience-preferences">
        <dl v-if="preferences.length" class="au-kv">
          <template v-for="preference in preferences" :key="preference.preference_id || `${preference.kind}-${preference.revision}`">
            <dt>{{ enumLabel('preferences.kinds', preference.kind) }}</dt>
            <dd>
              {{ preferenceValue(preference) }}
              <span class="au-muted"> · {{ enumLabel('preferences.scopes', preference.scope) }} · {{ preferenceSource(preference) }}</span>
            </dd>
          </template>
        </dl>
        <p v-else class="au-empty">{{ tr('empty') }}</p>
      </AuditCard>
    </div>
    <AuditCard :title="tr('director.deliveries')" :hint="tr('director.deliveriesHint')">
      <Table v-if="deliveries.length" :columns="columns" :rows="deliveries" row-key="key">
        <template #seq="{ row }">{{ tr('sources.turn', { count: row.seq }) }}</template>
        <template #delivery="{ row }">
          <span :title="statusHint(row.delivery?.status)">{{ statusLabel(row.delivery?.status) }}</span>
          <AuditId v-if="row.delivery?.guidance_id" :id="row.delivery.guidance_id" />
        </template>
        <template #passed="{ row }">{{ passedLabel(row.delivery) }}</template>
        <template #reason="{ row }">{{ row.delivery?.reason ? enumLabel('director.reasons', row.delivery.reason) : tr('director.notRecorded') }}</template>
      </Table>
      <p v-else class="au-empty">{{ tr('empty') }}</p>
    </AuditCard>
  </div>
</template>
